import { useCallback, useMemo, useState, type ReactNode } from "react"

import { ColumnReorderMobileDialog } from "@/components/ColumnReorderMobileDialog"
import { ColumnResizeHandle } from "@/components/ColumnResizeHandle"
import { AdvertiserStatusCell } from "@/components/AdvertiserStatusCell"
import { moveColumnAdjacent } from "@/lib/columnReorder"
import type {
  AnalyticsTotals,
  AccountAnalyticsRow,
  BuyerAnalyticsRow,
} from "@/api/hooks/dashboard"
import {
  formatCurrency,
  formatNumber,
  formatPercent,
} from "../lib/formatters"
import type { DashboardColumn } from "../lib/columns"
import {
  getDashboardRoiRowClass,
  ROI_ROW_STYLES,
  type DashboardRoiClass,
} from "../lib/roi"
import {
  loadPinnedColumnWidth,
  PINNED_COL_WIDTH_MAX,
  PINNED_COL_WIDTH_MIN,
  pinnedColWidthKeyForTable,
  savePinnedColumnWidth,
} from "../lib/columnWidths"

/**
 * Shared shell for the buyer + account analytics tables. Owns:
 *
 *   - Sticky <thead> with sortable headers + ▲/▼ indicators.
 *   - HTML5 drag-and-drop column reorder (pinned first column is
 *     immovable — drops onto it are rejected, and it cannot be
 *     dragged itself).
 *   - Row rendering with ROI-class tints.
 *   - Sticky <tfoot> totals row.
 *
 * The row/cell render logic is type-parametric so the same shell
 * handles `BuyerAnalyticsRow` and `AccountAnalyticsRow`.
 */

export type DashboardRow = BuyerAnalyticsRow | AccountAnalyticsRow

interface AnalyticsTableShellProps<Row extends DashboardRow> {
  tableId: string
  columns: readonly DashboardColumn[]
  rows: Row[]
  totals: AnalyticsTotals | null
  pinnedColumnId: string
  columnOrder: string[]
  sortKey: string
  sortDir: "asc" | "desc"
  onSort: (key: string, columnType: "string" | "number") => void
  onColumnOrderChange: (order: string[]) => void
  renderFirstCell: (row: Row) => ReactNode
  renderFirstTotalsCell: () => ReactNode
  isLoading?: boolean
}

/** Generic value cell — delegates to currency / number / percent. */
function defaultCellContent(col: DashboardColumn, row: Record<string, unknown>): ReactNode {
  const v = row[col.id] as number | null | undefined
  if (col.id === "tiktok_url") {
    const url = row[col.id] as string | null | undefined
    if (!url) return ""
    return (
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center rounded-md border border-[#667eea]/40 bg-[#667eea]/20 px-2 py-1 text-[10px] font-semibold text-[#c9d1ff] hover:bg-[#667eea]/30"
      >
        Open
      </a>
    )
  }
  if (col.id === "advertiser_status") {
    if (row.deleted_at) {
      return (
        <span className="inline-flex items-center rounded-full border border-gray-600/40 bg-gray-800/60 px-2 py-0.5 text-xs font-semibold text-gray-300">
          Deleted
        </span>
      )
    }
    const status = row[col.id] as string | null | undefined
    if (!status) return ""
    return <AdvertiserStatusCell status={status} />
  }
  if (col.id === "accounts_active") {
    const active = row.accounts_active as number | null | undefined
    const total = row.accounts_total as number | null | undefined
    if (active == null && total == null) return ""
    return `${active ?? 0}/${total ?? 0}`
  }
  if (col.id === "active_campaigns") {
    return formatNumber((row[col.id] as number | null | undefined) ?? null)
  }
  if (col.type === "string") return (v as unknown as string) ?? ""
  if (
    col.id === "cost" ||
    col.id === "revenue" ||
    col.id === "profit" ||
    col.id === "balance" ||
    col.id === "cpc" ||
    col.id === "cpm" ||
    col.id === "cpa" ||
    col.id === "rpc"
  ) {
    return formatCurrency(v ?? null)
  }
  if (col.id === "roi" || col.id === "cvr" || col.id === "ctr") {
    return formatPercent(v ?? null)
  }
  return formatNumber(v ?? null)
}

export function AnalyticsTableShell<Row extends DashboardRow>({
  tableId,
  columns,
  rows,
  totals,
  pinnedColumnId,
  columnOrder,
  sortKey,
  sortDir,
  onSort,
  onColumnOrderChange,
  renderFirstCell,
  renderFirstTotalsCell,
  isLoading,
}: AnalyticsTableShellProps<Row>) {
  const [dragId, setDragId] = useState<string | null>(null)
  const [dragOverId, setDragOverId] = useState<string | null>(null)
  const [columnReorderOpen, setColumnReorderOpen] = useState(false)
  const pinnedWidthKey = pinnedColWidthKeyForTable(tableId)
  const [pinnedColWidth, setPinnedColWidth] = useState(() =>
    loadPinnedColumnWidth(pinnedWidthKey),
  )
  const pinnedCellStyle = {
    width: pinnedColWidth,
    minWidth: pinnedColWidth,
    maxWidth: pinnedColWidth,
  } as const

  const columnsById = useMemo(() => {
    const m = new Map<string, DashboardColumn>()
    for (const c of columns) m.set(c.id, c)
    return m
  }, [columns])

  const orderedColumns = useMemo(
    () => columnOrder.map((id) => columnsById.get(id)).filter(Boolean) as DashboardColumn[],
    [columnOrder, columnsById],
  )

  const colCount = orderedColumns.length

  const draggableForReorder = useMemo(
    () => orderedColumns.filter((c) => c.id !== pinnedColumnId),
    [orderedColumns, pinnedColumnId],
  )

  const handleHeaderClick = useCallback(
    (col: DashboardColumn) => {
      onSort(col.id, col.type)
    },
    [onSort],
  )

  const handleDragStart = useCallback(
    (e: React.DragEvent<HTMLTableCellElement>, col: DashboardColumn) => {
      if (col.id === pinnedColumnId) {
        e.preventDefault()
        return
      }
      e.dataTransfer.setData("text/plain", col.id)
      e.dataTransfer.effectAllowed = "move"
      setDragId(col.id)
    },
    [pinnedColumnId],
  )

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLTableCellElement>, col: DashboardColumn) => {
      if (col.id === pinnedColumnId) return
      e.preventDefault()
      e.dataTransfer.dropEffect = "move"
      if (dragId !== col.id) setDragOverId(col.id)
    },
    [dragId, pinnedColumnId],
  )

  const handleDragLeave = useCallback(() => {
    setDragOverId(null)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLTableCellElement>, col: DashboardColumn) => {
      e.preventDefault()
      setDragOverId(null)
      const fromId = e.dataTransfer.getData("text/plain")
      setDragId(null)
      if (!fromId || fromId === col.id || fromId === pinnedColumnId) return
      const toIdx = columnOrder.indexOf(col.id)
      const fromIdx = columnOrder.indexOf(fromId)
      if (fromIdx === -1 || toIdx === -1 || toIdx === 0) return
      const next = columnOrder.slice()
      const [rem] = next.splice(fromIdx, 1)
      next.splice(toIdx, 0, rem)
      // Ensure pinned column stays at position 0.
      if (next[0] !== pinnedColumnId) {
        const pinnedIdx = next.indexOf(pinnedColumnId)
        if (pinnedIdx > 0) {
          next.splice(pinnedIdx, 1)
          next.unshift(pinnedColumnId)
        }
      }
      onColumnOrderChange(next)
    },
    [columnOrder, onColumnOrderChange, pinnedColumnId],
  )

  const handleDragEnd = useCallback(() => {
    setDragId(null)
    setDragOverId(null)
  }, [])

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="mb-1.5 flex shrink-0 justify-end md:hidden">
        <button
          type="button"
          onClick={() => setColumnReorderOpen(true)}
          className="rounded-md border border-[#555] bg-[#3a3a3a] px-3 py-1.5 text-xs font-medium text-[#e8e8e8] shadow-sm hover:bg-[#454545]"
        >
          Reorder columns
        </button>
      </div>
      <ColumnReorderMobileDialog
        open={columnReorderOpen}
        onOpenChange={setColumnReorderOpen}
        title="Reorder columns"
        items={draggableForReorder.map((c) => ({ id: c.id, label: c.label }))}
        onMoveUp={(i) => {
          const col = draggableForReorder[i]
          if (!col) return
          const next = moveColumnAdjacent(
            columnOrder,
            col.id,
            -1,
            pinnedColumnId,
          )
          if (next) onColumnOrderChange(next)
        }}
        onMoveDown={(i) => {
          const col = draggableForReorder[i]
          if (!col) return
          const next = moveColumnAdjacent(
            columnOrder,
            col.id,
            1,
            pinnedColumnId,
          )
          if (next) onColumnOrderChange(next)
        }}
      />
      <div className="relative min-h-0 flex-1 overflow-auto overscroll-x-contain rounded-md border border-[#4a4a4a] bg-[#2d2d2d] [-webkit-overflow-scrolling:touch]">
      <table id={tableId} className="w-max min-w-full border-collapse text-sm">
        <thead className="sticky top-0 z-10">
          <tr>
            {orderedColumns.map((col) => {
              const isPinned = col.id === pinnedColumnId
              const isActiveSort = sortKey === col.id
              const draggable = !isPinned
              const headerClasses = [
                "touch-manipulation cursor-pointer select-none whitespace-nowrap border-b border-[#4a4a4a] bg-[#404040] px-3 py-2.5 text-left text-xs font-semibold text-[#d0d0d0] md:touch-auto",
                isPinned
                  ? "sticky left-0 z-20 relative box-border border-r border-[#2a2a2a] bg-[#404040] pr-3 shadow-[4px_0_8px_-2px_rgba(0,0,0,0.35)]"
                  : "",
                dragId === col.id ? "opacity-40" : "",
                dragOverId === col.id ? "outline outline-1 outline-[#667eea]" : "",
              ]
                .filter(Boolean)
                .join(" ")
              return (
                <th
                  key={col.id}
                  className={headerClasses}
                  style={isPinned ? pinnedCellStyle : undefined}
                  title={
                    isPinned
                      ? `${col.label} — drag the right edge to resize (touch supported)`
                      : col.tooltip
                  }
                  data-sort={col.id}
                  data-type={col.type}
                  draggable={draggable}
                  onClick={() => handleHeaderClick(col)}
                  onDragStart={(e) => handleDragStart(e, col)}
                  onDragOver={(e) => handleDragOver(e, col)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, col)}
                  onDragEnd={handleDragEnd}
                >
                  <span className="inline-flex items-center gap-1">
                    {!isPinned ? (
                      <span
                        className="cursor-grab text-[#666]"
                        aria-hidden
                        onClick={(e) => e.stopPropagation()}
                      >
                        &#8942;&#8942;
                      </span>
                    ) : null}
                    <span className={isPinned ? "block truncate pr-1" : undefined}>
                      {col.label}
                    </span>
                    {isActiveSort ? (
                      <span className="text-[0.7rem] text-[#8b9aff]" aria-hidden>
                        {sortDir === "asc" ? "\u25B2" : "\u25BC"}
                      </span>
                    ) : null}
                  </span>
                  {isPinned ? (
                    <ColumnResizeHandle
                      min={PINNED_COL_WIDTH_MIN}
                      max={PINNED_COL_WIDTH_MAX}
                      startWidth={pinnedColWidth}
                      onResize={setPinnedColWidth}
                      onResizeEnd={(w) => savePinnedColumnWidth(pinnedWidthKey, w)}
                    />
                  ) : null}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td
                colSpan={colCount}
                className="p-4 text-center text-[#b0b0b0]"
              >
                Loading...
              </td>
            </tr>
          ) : rows.length === 0 ? (
            <tr>
              <td
                colSpan={colCount}
                className="p-4 text-center text-[#b0b0b0]"
              >
                No data for selected period
              </td>
            </tr>
          ) : (
            rows.map((row, rowIdx) => {
              const roiClass: DashboardRoiClass = getDashboardRoiRowClass(
                row.roi,
              )
              const style = ROI_ROW_STYLES[roiClass]
              return (
                <tr
                  key={`${rowIdx}-${(row as unknown as { user_id?: number; advertiser_id?: string }).user_id ?? (row as unknown as { advertiser_id?: string }).advertiser_id ?? rowIdx}`}
                  className={roiClass}
                  style={{ backgroundColor: style.row }}
                >
                  {orderedColumns.map((col) => {
                    const isPinned = col.id === pinnedColumnId
                    const content = isPinned
                      ? renderFirstCell(row)
                      : defaultCellContent(
                          col,
                          row as unknown as Record<string, unknown>,
                        )
                    return (
                      <td
                        key={col.id}
                        className={`border-b border-[#3a3a3a] px-3 py-2.5 text-[#e0e0e0] ${
                          isPinned
                            ? "sticky left-0 z-[5] box-border border-r border-[#1f1f1f] font-semibold shadow-[4px_0_8px_-2px_rgba(0,0,0,0.25)]"
                            : "whitespace-nowrap"
                        }`}
                        style={
                          isPinned
                            ? { ...pinnedCellStyle, backgroundColor: style.pinned }
                            : undefined
                        }
                      >
                        {isPinned ? (
                          <div className="min-w-0 truncate">{content}</div>
                        ) : (
                          content
                        )}
                      </td>
                    )
                  })}
                </tr>
              )
            })
          )}
        </tbody>
        {totals ? (
          <tfoot className="sticky bottom-0 z-10">
            <tr
              className="totals-row"
              style={{
                backgroundColor: "#383838",
                borderTop: "2px solid #7488e8",
              }}
            >
              {orderedColumns.map((col) => {
                const isPinned = col.id === pinnedColumnId
                const content = isPinned
                  ? renderFirstTotalsCell()
                  : defaultCellContent(
                      col,
                      totals as unknown as Record<string, unknown>,
                    )
                return (
                  <td
                    key={col.id}
                    className={`px-3 py-2.5 font-semibold text-[#e0e0e0] ${
                      isPinned
                        ? "sticky left-0 z-[5] box-border border-r border-[#2a2a2a] shadow-[4px_0_8px_-2px_rgba(0,0,0,0.25)]"
                        : "whitespace-nowrap"
                    }`}
                    style={
                      isPinned
                        ? {
                            ...pinnedCellStyle,
                            backgroundColor: "#383838",
                            borderTop: "2px solid #7488e8",
                          }
                        : { borderTop: "2px solid #7488e8" }
                    }
                  >
                    {isPinned ? (
                      <div className="min-w-0 truncate">{content}</div>
                    ) : (
                      content
                    )}
                  </td>
                )
              })}
            </tr>
          </tfoot>
        ) : null}
      </table>
    </div>
    </div>
  )
}
